import {formatDate} from '@angular/common';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {EntityType, HistoryEventType} from '@npck/af-frontend-api';

const DATE_FIELDS = new Set([
    'date', 'createdDate', 'updatedDate', 'deletedDate', 'rejectDateTime',
    'refundDateTime', 'kuiNumberDate', 'lawArticleDate', 'originDate',
    'unifiedRegisterDate', 'contactDate', 'statusAssignedDate',
    'activationDate', 'terminationDate', 'victimAppealDate',
]);

const AMOUNT_FIELDS = new Set([
    'amount', 'refundAmount', 'transactionAmount', 'resolutionAmount',
    'amountLossCredit', 'amountLossOwn', 'amountSaveCredit', 'amountSaveOwn',
    'balance', 'loanAmount', 'loanBalance',
]);

const ENUM_FIELDS = new Set([
    'status', 'type', 'fraudType', 'initialSubtype', 'basedOn',
    'refundStatus', 'deletedBase', 'subjectType', 'participantType', 'roleType',
    'transactionType', 'grounds', 'rejectType',
]);

interface SolutionInfoValue {
    solutions?: Array<{
        fullName?: string | null;
        category?: string;
        isChanged?: boolean;
    }>;
}

@Injectable({providedIn: 'root'})
export class ValueFormatter {
    constructor(private translate: TranslateService) {}

    format(entityType: EntityType, property: string, value: unknown, eventType?: HistoryEventType): string {
        if (value === null || value === undefined) {
            return '—';
        }
        if (entityType === EntityType.RESOLUTION && property === 'solutionInfo') {
            return this.formatSolutionInfo(value, eventType);
        }
        if (entityType === EntityType.RESOLUTION && property === 'category') {
            return this.translateCategory(value);
        }
        if (property === 'participantType' || property === 'roleType') {
            return this.translateParticipantType(value);
        }
        if (property === 'creationMethod') {
            return this.translateCreationMethod(value);
        }
        if (ENUM_FIELDS.has(property)) {
            const key = `i18n_af_enum_${entityType}_${property}_${value}`;
            const translated = this.translate.instant(key);
            return translated === key ? String(value) : translated;
        }
        if (DATE_FIELDS.has(property)) {
            try {
                const str = String(value);
                const hasTime = /T|\d{2}:\d{2}/.test(str);
                return formatDate(str, hasTime ? 'dd.MM.yyyy HH:mm:ss' : 'dd.MM.yyyy', 'ru');
            } catch {
                return String(value);
            }
        }
        if (AMOUNT_FIELDS.has(property)) {
            const numeric = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numeric)) {
                return this.formatTiynAsTenge(numeric);
            }
        }
        if (typeof value === 'boolean') {
            return this.translate.instant(value ? 'i18n_af_labelYes' : 'i18n_af_labelNo');
        }
        if (Array.isArray(value)) {
            return value.length === 0 ? '—' : value.map(v => String(v)).join(', ');
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    }

    private formatSolutionInfo(value: unknown, eventType?: HistoryEventType): string {
        const allSolutions = (value as SolutionInfoValue)?.solutions ?? [];
        const solutions = eventType === HistoryEventType.CREATED
            ? allSolutions
            : allSolutions.filter(s => s.isChanged === true);
        if (solutions.length === 0) {
            return '—';
        }
        const fullNameLabel = this.translate.instant('i18n_af_field_RESOLUTION_fullName');
        const categoryLabel = this.translate.instant('i18n_af_field_RESOLUTION_category');
        return solutions
            .map(s => {
                const fullName = s.fullName ?? '—';
                const category = this.translateCategory(s.category);
                return `${fullNameLabel}: ${fullName}\n${categoryLabel}: ${category}`;
            })
            .join('\n\n');
    }

    private translateCategory(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        const key = `i18n_af_category#${value}`;
        const translated = this.translate.instant(key);
        return translated === key ? String(value) : translated;
    }

    private translateCreationMethod(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        const key = `i18n_af_creationMethod#${value}`;
        const translated = this.translate.instant(key);
        return translated === key ? String(value) : translated;
    }

    private formatTiynAsTenge(tiyn: number): string {
        if (!tiyn) {return '0';}
        const tenge = (tiyn / 100).toFixed(2);
        return tenge.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    private translateParticipantType(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        const raw = String(value);
        const titleCase = raw.charAt(0) + raw.slice(1).toLowerCase();
        const key = `i18n_af_labelParticipant${titleCase}`;
        const translated = this.translate.instant(key);
        return translated === key ? raw : translated;
    }
}
